import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Trash2 } from "lucide-react"
import * as React from "react"

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@/components/ui"
import { useDeleteProvider, useProvider, useUpdateProvider } from "@/features/providers/api"
import { fieldErrorsFrom, formErrorFrom, type FieldErrors } from "@/features/providers/errors"
import { ConfirmDialog } from "@/components/modal"
import {
  missingRequired,
  ProviderForm,
  toFormValues,
  toPayload,
  type ProviderFormValues,
} from "@/features/providers/provider-form"
import { STATUS_LABELS, STATUS_TONES } from "@/lib/constants"
import { useSession } from "@/lib/session"
import { formatDateTime } from "@/lib/utils"
import { DocumentsPanel } from "@/features/documents/panel"

export const Route = createFileRoute("/providers/$providerId")({
  component: ProviderDetailScreen,
})

function ProviderDetailScreen() {
  const { providerId } = Route.useParams()
  const id = Number(providerId)

  if (!Number.isInteger(id)) {
    return (
      <>
        <BackLink />
        <ErrorState
          title="That is not a provider"
          description="The address does not contain a provider number."
        />
      </>
    )
  }

  return <ProviderDetail id={id} />
}

function BackLink() {
  return (
    <Link
      to="/providers"
      className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      All providers
    </Link>
  )
}

function ProviderDetail({ id }: { id: number }) {
  const navigate = useNavigate()
  const { canWrite } = useSession()
  const query = useProvider(id)
  const update = useUpdateProvider(id)
  const remove = useDeleteProvider()

  const [values, setValues] = React.useState<ProviderFormValues | null>(null)
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [message, setMessage] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)
  const [confirmingDelete, setConfirmingDelete] = React.useState(false)

  const provider = query.data

  // The server's copy is the truth this form starts from, and it re-seeds after
  // a save because the response carries what was actually stored.
  React.useEffect(() => {
    if (provider) setValues(toFormValues(provider))
  }, [provider])

  if (query.isLoading || (query.isSuccess && !values)) {
    return (
      <>
        <BackLink />
        <div aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading provider</span>
          <Skeleton className="mb-6 h-8 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </>
    )
  }

  if (query.isError || !provider || !values) {
    return (
      <>
        <BackLink />
        <ErrorState
          title="That provider could not be loaded"
          description="It may have been deleted, or the connection failed."
          onRetry={() => void query.refetch()}
        />
      </>
    )
  }

  const stored = toFormValues(provider)
  const dirty = (Object.keys(stored) as (keyof ProviderFormValues)[]).some(
    (key) => stored[key] !== values[key],
  )

  function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!values) return
    setErrors({})
    setMessage(null)
    setSaved(false)
    // The same guard the create dialog runs: the server does not yet refuse a
    // blank name, so without this the detail screen could blank one on save.
    const missing = missingRequired(values)
    if (Object.keys(missing).length > 0) {
      setErrors(missing)
      return
    }
    update.mutate(toPayload(values), {
      onSuccess: () => setSaved(true),
      onError: (cause) => {
        setErrors(fieldErrorsFrom(cause))
        setMessage(formErrorFrom(cause, "The provider could not be saved."))
      },
    })
  }

  function onDelete() {
    remove.mutate(id, {
      onSuccess: () => void navigate({ to: "/providers", replace: true }),
      onError: (cause) => {
        setConfirmingDelete(false)
        setMessage(formErrorFrom(cause, "The provider could not be deleted."))
      },
    })
  }

  return (
    <>
      <BackLink />

      <PageHeader
        title={provider.name}
        description={provider.location || undefined}
        actions={
          canWrite ? (
            <Button variant="secondary" onClick={() => setConfirmingDelete(true)}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Delete
            </Button>
          ) : undefined
        }
      />

      <Card className="max-w-3xl">
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Provider details</CardTitle>
          <Badge tone={STATUS_TONES[provider.status] ?? "neutral"}>
            {STATUS_LABELS[provider.status] ?? provider.status}
          </Badge>
        </CardHeader>

        <CardContent>
          <form onSubmit={onSubmit} noValidate>
            <p aria-live="polite" className="sr-only">
              {update.isPending ? "Saving" : saved ? "Provider saved" : ""}
            </p>

            {message && (
              <p
                role="alert"
                className="mb-4 rounded-md border border-destructive px-3 py-2 text-sm text-destructive"
              >
                {message}
              </p>
            )}

            {saved && !dirty && (
              <p className="mb-4 rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success">
                Saved.
              </p>
            )}

            <ProviderForm
              idPrefix="provider"
              values={values}
              errors={errors}
              disabled={!canWrite}
              onChange={(next) => {
                setValues(next)
                setSaved(false)
              }}
            />

            {canWrite && (
              <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                <Button type="submit" loading={update.isPending} disabled={!dirty}>
                  Save changes
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!dirty || update.isPending}
                  onClick={() => {
                    setValues(toFormValues(provider))
                    setErrors({})
                    setMessage(null)
                  }}
                >
                  Discard changes
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      <dl className="mt-6 max-w-3xl grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div className="flex justify-between gap-4 border-b py-2">
          <dt className="text-muted-foreground">Created</dt>
          <dd>{formatDateTime(provider.created_at)}</dd>
        </div>
        <div className="flex justify-between gap-4 border-b py-2">
          <dt className="text-muted-foreground">Last modified</dt>
          <dd>{formatDateTime(provider.modified_at)}</dd>
        </div>
      </dl>

      <div className="mt-6 max-w-3xl">
        <DocumentsPanel parentType="providers" parentId={id} />
      </div>

      {confirmingDelete && (
        <ConfirmDialog
          title="Delete this provider?"
          description={`${provider.name} will be removed. Training events that name this provider may be left without one.`}
          confirmLabel="Delete provider"
          pending={remove.isPending}
          onConfirm={onDelete}
          onClose={() => setConfirmingDelete(false)}
        />
      )}
    </>
  )
}
